// @ts-check
// ── Monthly payments after a paid-in-full program, set up at sign-up ───────────
//
// Clayton, 10/13: no Stripe free trials, "build it however is the best way". Add Client and Renew used to make a Stripe
// subscription with a free trial that ended on the start day (create-continuity-subscription, retired): Stripe could
// email the client that their trial was ending and print "TRIAL OVER" on their statement, and on the start day nothing
// made them a monthly client or gave them that month's sessions. Now, once the paid-in-full charge has gone through and
// the client is saved, Change billing sets up a switch to monthly that starts the day after the program ends, on the
// card just charged (coach-billing-plan.js start-after-program). From then on it is under Coming up in Change billing,
// with its Undo, and it moves with the program end when the client picks their start on the onboarding form.
import { callFunction } from './api.js';
import { formatCurrency } from './utils.js';

/**
 * The continuity rate typed at sign-up comes off the record before it is saved: the sessions rules read a rate on file
 * as "no end", so the daily job would hand out the first monthly month before Stripe charged it, and the daily
 * continuity check would email the client that a payment was due. It goes to Change billing instead.
 * @param {Record<string, any>} data the client about to be saved @returns {number} the rate set aside, 0 for none
 */
export function setContinuityAside(data) {
  const rate = Number(data.continuityRate) || 0;
  data.continuityRate = null;
  return rate;
}

/**
 * Ask Change billing for the switch to monthly. The paid-in-full charge is the request key, so a second press makes no
 * second schedule.
 * @param {{ clientId: string, cardId: string, rate: number, paymentIntentId: string }} a
 * @returns {Promise<string|null>} null once it is set up; otherwise what Change billing said
 */
export async function startMonthlyAfterProgram({ clientId, cardId, rate, paymentIntentId }) {
  try {
    await callFunction('coach-billing-plan', {
      action: 'start-after-program', clientId, cardId, amountCents: Math.round(rate * 100), requestKey: `continuity:${paymentIntentId}`,
    });
    return null;
  } catch (err) {
    return (err && /** @type {any} */ (err).message) || 'Change billing did not answer.';
  }
}

/**
 * The words for the coach when the charge went through but monthly could not be set up. The client is saved and paid
 * in full; nothing will charge them after the program until it is set up. The rate is in the words: it is no longer on
 * the record (final review, 10/13).
 * @param {string} name @param {string} why what Change billing said @param {number} rate dollars a month
 */
export const notSetUpWords = (name, why, rate) =>
  `${name} paid in full, but their monthly payments of ${formatCurrency(rate)} after the program were not set up: ${why} Set them up in their Change billing.`;
